import {useState, useCallback, useRef, useEffect} from 'react';
import {Car} from '../types';
import {carService} from '../services';

export type FocusMode = 'voyage' | 'global';
export type FocusType = 'refNo' | 'chassis' | 'shipper' | 'consignee';

export interface UseFocusModeOptions {
    mode: FocusMode;
    voyageId?: string; // Required when mode='voyage'
    cars?: Car[]; // For client-side fallback when no API available
    onError?: (message: string) => void;
    tableId?: string; // For localStorage persistence
}

interface FocusPreference {
    type: FocusType;
    value: string | number;
}

const getStorageKey = (tableId: string) => `table-prefs-${tableId}`;

function loadFocusPreference(tableId: string): FocusPreference | null {
    try {
        const stored = localStorage.getItem(getStorageKey(tableId));
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (parsed.focus && parsed.focus.type && parsed.focus.value !== undefined) {
            return parsed.focus;
        }
        return null;
    } catch {
        return null;
    }
}

function saveFocusPreference(tableId: string, focus: FocusPreference | null): void {
    try {
        const key = getStorageKey(tableId);
        const existing = localStorage.getItem(key);
        let data: Record<string, unknown> = {};
        if (existing) {
            try {
                data = JSON.parse(existing);
            } catch {
                data = {};
            }
        }
        if (focus) {
            data.focus = focus;
        } else {
            delete data.focus;
        }
        localStorage.setItem(key, JSON.stringify(data));
    } catch {
        // Ignore storage errors
    }
}

export interface UseFocusModeReturn {
    // State
    focusedRefNo: number | null;
    focusedChassis: string | null;
    focusedShipper: string | null;
    focusedConsignee: string | null;
    focusedCars: Car[] | null;
    focusLoading: boolean;

    // Actions
    handleFocus: (car: Car) => Promise<void>;
    handleFocusChassis: (car: Car) => Promise<void>;
    handleFocusShipper: (car: Car) => Promise<void>;
    handleFocusConsignee: (car: Car) => Promise<void>;
    exitFocusMode: () => void;

    // Derived
    isFocused: boolean;
    focusLabel: string | null;
    focusCount: number;
}

/**
 * Hook to manage focus mode state for car tables.
 * Supports both voyage-scoped and global focus modes.
 *
 * - `mode='voyage'`: Uses voyage-specific APIs (requires voyageId)
 * - `mode='global'`: Uses global APIs (no voyageId needed)
 *
 * Falls back to client-side filtering if no voyageId is provided in voyage mode.
 */
export function useFocusMode({
    mode,
    voyageId,
    cars = [],
    onError,
    tableId,
}: UseFocusModeOptions): UseFocusModeReturn {
    const [focusedRefNo, setFocusedRefNo] = useState<number | null>(null);
    const [focusedChassis, setFocusedChassis] = useState<string | null>(null);
    const [focusedShipper, setFocusedShipper] = useState<string | null>(null);
    const [focusedConsignee, setFocusedConsignee] = useState<string | null>(null);
    const [focusedCars, setFocusedCars] = useState<Car[] | null>(null);
    const [focusLoading, setFocusLoading] = useState(false);

    // Request counter to handle race conditions - only apply results from the latest request
    const requestIdRef = useRef(0);
    // Track if we've restored focus from localStorage
    const hasRestoredRef = useRef(false);

    // Clear all focus states before starting a new focus operation
    const clearAllFocusStates = useCallback(() => {
        setFocusedRefNo(null);
        setFocusedChassis(null);
        setFocusedShipper(null);
        setFocusedConsignee(null);
        setFocusedCars(null);
    }, []);

    // Client-side fallback - filters cars around the target value
    const clientSideFocus = useCallback((
        sortedCars: Car[],
        targetIndex: number,
        countBefore: number = 50,
        countAfter: number = 50
    ): Car[] => {
        if (targetIndex === -1) return [];
        const startIndex = Math.max(0, targetIndex - countBefore);
        const endIndex = Math.min(sortedCars.length, targetIndex + countAfter + 1);
        return sortedCars.slice(startIndex, endIndex);
    }, []);

    // Focus by reference number (internal - accepts value directly)
    const focusByRefNo = useCallback(async (refNo: number) => {
        const currentRequestId = ++requestIdRef.current;
        clearAllFocusStates();
        setFocusLoading(true);
        setFocusedRefNo(refNo);

        try {
            let focusedData: Car[];
            if (mode === 'global') {
                const response = await carService.getFocusedCarsGlobal(refNo, 50, 50);
                focusedData = response.data as Car[];
            } else if (voyageId) {
                const response = await carService.getFocusedCars(voyageId, refNo, 50, 50);
                focusedData = response.data as Car[];
            } else {
                const sortedCars = [...cars].sort((a, b) => a.reference_number - b.reference_number);
                const targetIndex = sortedCars.findIndex(c => c.reference_number === refNo);
                focusedData = clientSideFocus(sortedCars, targetIndex);
            }
            if (currentRequestId === requestIdRef.current) {
                setFocusedCars(focusedData);
            }
        } catch (error) {
            console.error('Error fetching focused cars:', error);
            if (currentRequestId === requestIdRef.current) {
                onError?.('Failed to load focused cars');
                setFocusedCars(null);
                setFocusedRefNo(null);
            }
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setFocusLoading(false);
            }
        }
    }, [mode, voyageId, cars, clearAllFocusStates, clientSideFocus, onError]);

    // Focus by chassis (internal - accepts value directly)
    const focusByChassis = useCallback(async (chassis: string) => {
        const currentRequestId = ++requestIdRef.current;
        clearAllFocusStates();
        setFocusLoading(true);
        setFocusedChassis(chassis);

        try {
            let focusedData: Car[];
            if (mode === 'global') {
                const response = await carService.getFocusedCarsByChassisGlobal(chassis, 50, 50);
                focusedData = response.data as Car[];
            } else if (voyageId) {
                const response = await carService.getFocusedCarsByChassis(voyageId, chassis, 50, 50);
                focusedData = response.data as Car[];
            } else {
                const sortedCars = [...cars].sort((a, b) =>
                    (a.chassis_number || '').localeCompare(b.chassis_number || '')
                );
                const targetIndex = sortedCars.findIndex(c => c.chassis_number === chassis);
                focusedData = clientSideFocus(sortedCars, targetIndex);
            }
            if (currentRequestId === requestIdRef.current) {
                setFocusedCars(focusedData);
            }
        } catch (error) {
            console.error('Error fetching focused cars by chassis:', error);
            if (currentRequestId === requestIdRef.current) {
                onError?.('Failed to load focused cars');
                setFocusedCars(null);
                setFocusedChassis(null);
            }
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setFocusLoading(false);
            }
        }
    }, [mode, voyageId, cars, clearAllFocusStates, clientSideFocus, onError]);

    // Focus by shipper (internal - accepts value directly)
    const focusByShipper = useCallback(async (shipper: string) => {
        const currentRequestId = ++requestIdRef.current;
        clearAllFocusStates();
        setFocusLoading(true);
        setFocusedShipper(shipper);

        try {
            let focusedData: Car[];
            if (mode === 'global') {
                const response = await carService.getFocusedCarsByShipperGlobal(shipper, 50, 50);
                focusedData = response.data as Car[];
            } else if (voyageId) {
                const response = await carService.getFocusedCarsByShipper(voyageId, shipper, 50, 50);
                focusedData = response.data as Car[];
            } else {
                const sortedCars = [...cars].sort((a, b) =>
                    (a.shipper_name || '').localeCompare(b.shipper_name || '')
                );
                const targetIndex = sortedCars.findIndex(c => c.shipper_name === shipper);
                focusedData = clientSideFocus(sortedCars, targetIndex);
            }
            if (currentRequestId === requestIdRef.current) {
                setFocusedCars(focusedData);
            }
        } catch (error) {
            console.error('Error fetching focused cars by shipper:', error);
            if (currentRequestId === requestIdRef.current) {
                onError?.('Failed to load focused cars');
                setFocusedCars(null);
                setFocusedShipper(null);
            }
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setFocusLoading(false);
            }
        }
    }, [mode, voyageId, cars, clearAllFocusStates, clientSideFocus, onError]);

    // Focus by consignee (internal - accepts value directly)
    const focusByConsignee = useCallback(async (consignee: string) => {
        const currentRequestId = ++requestIdRef.current;
        clearAllFocusStates();
        setFocusLoading(true);
        setFocusedConsignee(consignee);

        try {
            let focusedData: Car[];
            if (mode === 'global') {
                const response = await carService.getFocusedCarsByConsigneeGlobal(consignee, 50, 50);
                focusedData = response.data as Car[];
            } else if (voyageId) {
                const response = await carService.getFocusedCarsByConsignee(voyageId, consignee, 50, 50);
                focusedData = response.data as Car[];
            } else {
                const sortedCars = [...cars].sort((a, b) =>
                    (a.consignee_name || '').localeCompare(b.consignee_name || '')
                );
                const targetIndex = sortedCars.findIndex(c => c.consignee_name === consignee);
                focusedData = clientSideFocus(sortedCars, targetIndex);
            }
            if (currentRequestId === requestIdRef.current) {
                setFocusedCars(focusedData);
            }
        } catch (error) {
            console.error('Error fetching focused cars by consignee:', error);
            if (currentRequestId === requestIdRef.current) {
                onError?.('Failed to load focused cars');
                setFocusedCars(null);
                setFocusedConsignee(null);
            }
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setFocusLoading(false);
            }
        }
    }, [mode, voyageId, cars, clearAllFocusStates, clientSideFocus, onError]);

    // Restore focus from localStorage on mount
    useEffect(() => {
        if (!tableId || hasRestoredRef.current) return;
        hasRestoredRef.current = true;

        const saved = loadFocusPreference(tableId);
        if (!saved) return;

        switch (saved.type) {
            case 'refNo':
                focusByRefNo(saved.value as number);
                break;
            case 'chassis':
                focusByChassis(saved.value as string);
                break;
            case 'shipper':
                focusByShipper(saved.value as string);
                break;
            case 'consignee':
                focusByConsignee(saved.value as string);
                break;
        }
    }, [tableId, focusByRefNo, focusByChassis, focusByShipper, focusByConsignee]);

    // Focus by reference number (public - saves to localStorage)
    const handleFocus = useCallback(async (car: Car) => {
        if (tableId) {
            saveFocusPreference(tableId, { type: 'refNo', value: car.reference_number });
        }
        await focusByRefNo(car.reference_number);
    }, [tableId, focusByRefNo]);

    // Focus by chassis number (public - saves to localStorage)
    const handleFocusChassis = useCallback(async (car: Car) => {
        if (!car.chassis_number) return;
        if (tableId) {
            saveFocusPreference(tableId, { type: 'chassis', value: car.chassis_number });
        }
        await focusByChassis(car.chassis_number);
    }, [tableId, focusByChassis]);

    // Focus by shipper name (public - saves to localStorage)
    const handleFocusShipper = useCallback(async (car: Car) => {
        if (!car.shipper_name) return;
        if (tableId) {
            saveFocusPreference(tableId, { type: 'shipper', value: car.shipper_name });
        }
        await focusByShipper(car.shipper_name);
    }, [tableId, focusByShipper]);

    // Focus by consignee name (public - saves to localStorage)
    const handleFocusConsignee = useCallback(async (car: Car) => {
        if (!car.consignee_name) return;
        if (tableId) {
            saveFocusPreference(tableId, { type: 'consignee', value: car.consignee_name });
        }
        await focusByConsignee(car.consignee_name);
    }, [tableId, focusByConsignee]);

    // Exit focus mode (clears localStorage)
    const exitFocusMode = useCallback(() => {
        if (tableId) {
            saveFocusPreference(tableId, null);
        }
        setFocusedRefNo(null);
        setFocusedChassis(null);
        setFocusedShipper(null);
        setFocusedConsignee(null);
        setFocusedCars(null);
    }, [tableId]);

    // Derived values
    const isFocused = focusedRefNo !== null ||
        focusedChassis !== null ||
        focusedShipper !== null ||
        focusedConsignee !== null;

    const focusLabel = focusedRefNo !== null
        ? `Ref #${focusedRefNo}`
        : focusedChassis !== null
            ? `Chassis: ${focusedChassis}`
            : focusedShipper !== null
                ? `Shipper: ${focusedShipper}`
                : focusedConsignee !== null
                    ? `Consignee: ${focusedConsignee}`
                    : null;

    const focusCount = focusedCars?.length || 0;

    return {
        // State
        focusedRefNo,
        focusedChassis,
        focusedShipper,
        focusedConsignee,
        focusedCars,
        focusLoading,

        // Actions
        handleFocus,
        handleFocusChassis,
        handleFocusShipper,
        handleFocusConsignee,
        exitFocusMode,

        // Derived
        isFocused,
        focusLabel,
        focusCount,
    };
}
